import React from "react";
import type {
  KpiActiveUsersComparison,
  KpiReportResponse,
  MonthlyKpiStats,
} from "../../api/kpi";
import { formatNumber, formatCurrency, formatInteger } from "../../helpers/formatNumber";
import { EMPTY_VALUE } from "../../helpers/dashboardMetrics";
import { KPI_GOALS } from "../../config/kpiGoals";
import { computeAllGoalProgress } from "../../helpers/kpiGoalCalculations";
import { GoalProgressSection } from "../KpiGoals";
import { Button } from "../Button";
import { ReactComponent as DownloadIcon } from "../../assets/icons/download.svg";

export const ACTIVE_USERS_FOOTNOTE =
  "Since July 2026, Active Users matches Analytics → Activity, which only counts people who accepted analytics cookies, so the real number is somewhat higher. It is updated once a day: the current month includes every day up to yesterday.";

export interface KpiReportViewProps {
  data: KpiReportResponse;
  title: string;
  description: React.ReactNode;
  csvFilePrefix: string;
  activeUsersComparison?: KpiActiveUsersComparison[];
}

type KpiSection = "users" | "transactions" | "games" | "downsizing";

// Display order of the sections. Rows are tagged with their section instead of
// being cut out of one list by position, so adding a row cannot shift the
// boundaries of the sections after it.
const SECTIONS: { key: KpiSection; label: string }[] = [
  { key: "users", label: "Users" },
  { key: "transactions", label: "Transactions" },
  { key: "games", label: "Games" },
  { key: "downsizing", label: "Downsizing" },
];

interface KpiRow {
  section: KpiSection;
  label: string;
  // null when the metric has no meaningful all-time figure; shown as a dash.
  allTime: number | null;
  monthly: { [month: string]: number };
  isPercentage?: boolean;
  isCurrency?: boolean;
  showPercentage?: boolean;
  // What the percentage is a share of, in the same month (or all time).
  percentageBase?: "totalUsers" | "activeUsers";
  // A row added after the report was first shown, drawn so it reads as new.
  isNew?: boolean;
  // Months where V2 changed this row's figure show the V1 value beside it.
  showsV1Comparison?: boolean;
}

const formatValue = (
  value: number, 
  isCurrency: boolean = false, 
  isPercentage: boolean = false,
  showPercentage: boolean = false,
  percentageValue?: number
): string => {
  let formattedValue: string;
  
  if (isCurrency) {
    formattedValue = formatCurrency(value);
  } else {
    formattedValue = formatInteger(value);
  }
  
  if (showPercentage && percentageValue !== undefined) {
    return `${formattedValue} (${formatNumber(percentageValue, 1)}%)`;
  }
  
  if (isPercentage) {
    return `${formatNumber(value, 1)}%`;
  }
  
  return formattedValue;
};

const calculatePercentage = (value: number, total: number): number => {
  if (total === 0) return 0;
  return (value / total) * 100;
};

const sortedMonthsOf = (data: KpiReportResponse): string[] =>
  data.monthlyStats.map((stat) => stat.month).sort((a, b) => b.localeCompare(a));

// The corrected report (Active Users from V2, no All Time active figure, purchasers
// shown against both bases) is only drawn when the backend says V2 is on. Until
// then the table and CSV stay exactly as the client has always seen them.
export const isV2Report = (data: KpiReportResponse): boolean =>
  data.activeUsersV2Enabled === true;

const prepareKpiRows = (data: KpiReportResponse): KpiRow[] => {
  const { monthlyStats, allTimeStats } = data;
  const v2 = isV2Report(data);

  // Sort months in descending order (newest first)
  const sortedMonths = sortedMonthsOf(data);

  // Create monthly data map
  const monthlyMap: { [month: string]: MonthlyKpiStats } = {};
  monthlyStats.forEach((stat) => {
    monthlyMap[stat.month] = stat;
  });

  // Only part of the corrected report: purchasers as a share of each month's
  // active users, highlighted because it is new.
  const purchasersVsActiveUsers: KpiRow = {
    section: "transactions",
    label: "Unique Purchasers vs Active Users",
    allTime: null,
    monthly: Object.fromEntries(
      sortedMonths.map((month) => [
        month,
        monthlyMap[month]?.uniquePurchasingUsers || 0,
      ])
    ),
    showPercentage: true,
    percentageBase: "activeUsers",
    isNew: true,
  };

  return [
    // Users Section
    {
      section: "users",
      label: "Total Users",
      allTime: allTimeStats.totalUsers,
      monthly: Object.fromEntries(
        sortedMonths.map((month) => [
          month,
          monthlyMap[month]?.totalUsers || 0,
        ])
      ),
    },
    {
      section: "users",
      label: "Active Users",
      // With V2 the all-time active count equals Total Users, which would read
      // as a meaningless 100%, so active users only make sense per month.
      allTime: v2 ? null : allTimeStats.totalActiveUsers,
      monthly: Object.fromEntries(
        sortedMonths.map((month) => [
          month,
          monthlyMap[month]?.activeUsers || 0,
        ])
      ),
      showPercentage: true,
      percentageBase: "totalUsers",
      showsV1Comparison: true,
    },
    {
      section: "users",
      label: "New Users",
      allTime: monthlyStats.reduce((sum, stat) => sum + stat.newUsers, 0),
      monthly: Object.fromEntries(
        sortedMonths.map((month) => [month, monthlyMap[month]?.newUsers || 0])
      ),
    },

    // Transactions Section
    {
      section: "transactions",
      label: "Transaction Amount",
      allTime: allTimeStats.totalTransactionAmount,
      monthly: Object.fromEntries(
        sortedMonths.map((month) => [
          month,
          monthlyMap[month]?.totalTransactionAmount || 0,
        ])
      ),
      isCurrency: true,
    },
    {
      section: "transactions",
      label: "Assigned Cashback",
      allTime: allTimeStats.totalAssignedCashback,
      monthly: Object.fromEntries(
        sortedMonths.map((month) => [
          month,
          monthlyMap[month]?.totalAssignedCashback || 0,
        ])
      ),
      isCurrency: true,
    },
    {
      section: "transactions",
      label: "Transaction Count",
      allTime: allTimeStats.totalTransactionCount,
      monthly: Object.fromEntries(
        sortedMonths.map((month) => [
          month,
          monthlyMap[month]?.transactionCount || 0,
        ])
      ),
    },
    {
      section: "transactions",
      label: v2 ? "Unique Purchasers vs Total Users" : "Unique Purchasing Users",
      allTime: allTimeStats.totalUniquePurchasingUsers,
      monthly: Object.fromEntries(
        sortedMonths.map((month) => [
          month,
          monthlyMap[month]?.uniquePurchasingUsers || 0,
        ])
      ),
      showPercentage: true,
      percentageBase: "totalUsers",
    },
    ...(v2 ? [purchasersVsActiveUsers] : []),

    // Games Section
    {
      section: "games",
      label: "Game Plays",
      allTime: allTimeStats.totalGamesPlayed,
      monthly: Object.fromEntries(
        sortedMonths.map((month) => [
          month,
          monthlyMap[month]?.totalGamesPlayed || 0,
        ])
      ),
    },
    {
      section: "games",
      label: "Won Games",
      allTime: allTimeStats.totalGamesWon,
      monthly: Object.fromEntries(
        sortedMonths.map((month) => [
          month,
          monthlyMap[month]?.totalGamesWon || 0,
        ])
      ),
    },
    {
      section: "games",
      label: "Reward Amount",
      allTime: allTimeStats.totalRewardAmount,
      monthly: Object.fromEntries(
        sortedMonths.map((month) => [
          month,
          monthlyMap[month]?.totalRewardAmount || 0,
        ])
      ),
      isCurrency: true,
    },
    {
      section: "games",
      label: "Unique Game Players",
      allTime: allTimeStats.totalUniqueGamePlayers,
      monthly: Object.fromEntries(
        sortedMonths.map((month) => [
          month,
          monthlyMap[month]?.uniqueGamePlayers || 0,
        ])
      ),
      showPercentage: true,
      percentageBase: "totalUsers",
    },

    // Downsizing Section
    {
      section: "downsizing",
      label: "Downsizing Amount",
      allTime: allTimeStats.totalDownsizingAmount,
      monthly: Object.fromEntries(
        sortedMonths.map((month) => [
          month,
          monthlyMap[month]?.totalDownsizingAmount || 0,
        ])
      ),
      isCurrency: true,
    },
    {
      section: "downsizing",
      label: "Downsizing Users",
      allTime: allTimeStats.totalUniqueDownsizingUsers,
      monthly: Object.fromEntries(
        sortedMonths.map((month) => [
          month,
          monthlyMap[month]?.uniqueDownsizingUsers || 0,
        ])
      ),
      showPercentage: true,
      percentageBase: "totalUsers",
    },
  ];
};

// The percentage shown beside a cell, or undefined when the row shows none.
// Total Users keeps its historical fallback of 1 for an empty base; Active Users
// has none, so a month without active users shows the count alone rather than a
// share of nothing.
const percentageFor = (
  row: KpiRow,
  value: number,
  stats: { totalUsers: number; activeUsers?: number } | undefined
): number | undefined => {
  if (!row.showPercentage) return undefined;
  if (row.percentageBase === "totalUsers") {
    return calculatePercentage(value, stats?.totalUsers || 1);
  }
  if (row.percentageBase === "activeUsers") {
    return stats?.activeUsers ? calculatePercentage(value, stats.activeUsers) : undefined;
  }
  return undefined;
};

const formatAllTime = (row: KpiRow, data: KpiReportResponse): string =>
  row.allTime === null
    ? EMPTY_VALUE
    : formatValue(
        row.allTime,
        row.isCurrency,
        row.isPercentage,
        row.showPercentage,
        percentageFor(row, row.allTime, data.allTimeStats)
      );

export const buildKpiCsv = (data: KpiReportResponse): string => {
  const rows = prepareKpiRows(data);
  const months = sortedMonthsOf(data);

  const header = ["Metric", "All Time", ...months];
  const csvLines: string[] = [header.join(",")];

  for (const section of SECTIONS) {
    csvLines.push([section.label, ...Array(months.length + 1).fill("")].join(","));
    for (const row of rows.filter((r) => r.section === section.key)) {
      const fmt = (v: number) => (row.isCurrency ? (v / 100).toFixed(2) : v);
      const allTimeVal = row.allTime === null ? EMPTY_VALUE : fmt(row.allTime);
      const monthlyVals = months.map((m) => fmt(row.monthly[m] ?? 0));
      csvLines.push([row.label, allTimeVal, ...monthlyVals].join(","));
    }
  }

  return csvLines.join("\n");
};

const KpiReportView: React.FC<KpiReportViewProps> = ({
  data,
  title,
  description,
  csvFilePrefix,
  activeUsersComparison,
}) => {
  const kpiRows = prepareKpiRows(data);
  const sortedMonths = sortedMonthsOf(data);
  const statsByMonth = new Map(data.monthlyStats.map((s) => [s.month, s]));

  const goalProgress = computeAllGoalProgress(KPI_GOALS, data);

  const comparisonByMonth = new Map(
    (activeUsersComparison ?? []).map((c) => [c.month, c])
  );

  const exportToCsv = () => {
    // The corrected report writes "—" for the All Time figure of Active Users. Excel
    // opens a CSV without a byte order mark as ANSI and garbles that character, so
    // the file starts with a UTF-8 BOM. The report as it was before V2 has no such
    // character and stays byte-for-byte what it has always been.
    const csv = buildKpiCsv(data);
    const blob = new Blob([isV2Report(data) ? `\uFEFF${csv}` : csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${csvFilePrefix}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-w-0">
      <div className="flex items-start justify-between mb-10">
        <div className="flex flex-col gap-4">
          <h2 className="font-medium">{title}</h2>
          <div className="text-sm text-gray-600">{description}</div>
        </div>
        <Button variant="outline" onClick={exportToCsv} className="gap-2 whitespace-nowrap">
          <DownloadIcon className="w-4 h-4" />
          Export CSV
        </Button>
      </div>

      <GoalProgressSection goals={goalProgress} />

      <div className="overflow-x-auto">
        <table className="table-default">
          <thead>
            <tr>
              <th className="sticky left-0 bg-white z-20 min-w-[250px] border-r-2 border-gray-300">
                Metric
              </th>
              <th className="sticky left-[250px] bg-blue-50 z-20 min-w-[150px] border-l-2 border-r-2 border-gray-300">
                All Time
              </th>
              {sortedMonths.map((month) => (
                <th
                  key={month}
                  className="min-w-[150px] whitespace-nowrap border-l border-gray-300"
                >
                  {month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SECTIONS.map((section) => {
              const rows = kpiRows.filter((row) => row.section === section.key);
              return (
                <React.Fragment key={section.key}>
                  <tr className="bg-gray-50 border-t-2 border-b-2 border-gray-300">
                    <td
                      colSpan={2}
                      className="font-semibold text-gray-700 py-2 sticky left-0 bg-gray-50 z-10 border-r-2 border-gray-300"
                    >
                      {section.label}
                    </td>
                    {sortedMonths.map((month) => (
                      <td
                        key={month}
                        className="bg-gray-50 border-l border-gray-300"
                      ></td>
                    ))}
                  </tr>
                  {rows.map((row, index) => (
                    <tr
                      key={row.label}
                      className={
                        index === rows.length - 1 ? "border-b-2 border-gray-300" : ""
                      }
                    >
                      <td
                        className={`sticky left-0 ${
                          row.isNew ? "bg-yellow-50" : "bg-white"
                        } z-10 pl-6 border-r-2 border-gray-300`}
                      >
                        {row.label}
                      </td>
                      <td
                        className={`sticky left-[250px] ${
                          row.isNew ? "bg-yellow-50" : "bg-blue-50"
                        } font-medium z-10 border-l-2 border-r-2 border-gray-300`}
                      >
                        {formatAllTime(row, data)}
                      </td>
                      {sortedMonths.map((month) => {
                        const comparison = row.showsV1Comparison
                          ? comparisonByMonth.get(month)
                          : undefined;
                        const changed = !!comparison && comparison.v1 !== comparison.v2;
                        const highlighted = changed || row.isNew;
                        return (
                          <td
                            key={month}
                            className={
                              highlighted
                                ? "border-l border-gray-300 bg-yellow-50"
                                : "border-l border-gray-300"
                            }
                            title={changed ? `Source: ${comparison.source}` : undefined}
                          >
                            {formatValue(
                              row.monthly[month] || 0,
                              row.isCurrency,
                              row.isPercentage,
                              row.showPercentage,
                              percentageFor(
                                row,
                                row.monthly[month] || 0,
                                statsByMonth.get(month)
                              )
                            )}
                            {changed && (
                              <div className="text-xs text-gray-400 line-through">
                                V1: {formatInteger(comparison.v1)}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {data.activeUsersV2Enabled && (
        <p className="text-sm text-gray-500 mt-4">{ACTIVE_USERS_FOOTNOTE}</p>
      )}
    </div>
  );
};

export default KpiReportView;
